import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, ShieldCheck, ShieldOff, QrCode, Key, Copy, Check,
  RefreshCw, AlertTriangle, Smartphone, X, Lock, Unlock
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function TwoFactorSettings({ adminId }) {
  const [status, setStatus] = useState({ enabled: false, backup_codes_remaining: 0 });
  const [loading, setLoading] = useState(true);
  const [setupMode, setSetupMode] = useState(false);
  const [disableMode, setDisableMode] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  const headers = { 'X-Admin-Id': adminId };

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/2fa/status`, { headers });
      setStatus(response.data);
    } catch (error) {
      console.error('Error fetching 2FA status:', error);
    } finally {
      setLoading(false);
    }
  }, [adminId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const startSetup = async () => {
    try {
      setProcessing(true);
      const response = await axios.post(`${API_URL}/api/admin/2fa/setup`, null, { headers });
      setSetupData(response.data);
      setSetupMode(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar configuración 2FA');
    } finally {
      setProcessing(false);
    }
  };

  const confirmSetup = async () => {
    if (!verificationCode || verificationCode.length < 6) {
      toast.error('Introduce un código válido de 6 dígitos');
      return;
    }

    try {
      setProcessing(true);
      const response = await axios.post(
        `${API_URL}/api/admin/2fa/confirm`,
        { code: verificationCode },
        { headers }
      );
      
      if (response.data.success) {
        setBackupCodes(response.data.backup_codes);
        setShowBackupCodes(true);
        setSetupMode(false);
        setSetupData(null);
        setVerificationCode('');
        toast.success('2FA activado correctamente');
        fetchStatus();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setProcessing(false);
    }
  };

  const disable2FA = async () => {
    if (!verificationCode || verificationCode.length < 6) {
      toast.error('Introduce un código válido de 6 dígitos');
      return;
    }

    try {
      setProcessing(true);
      const response = await axios.post(
        `${API_URL}/api/admin/2fa/disable`,
        { code: verificationCode },
        { headers }
      );
      
      if (response.data.success) {
        toast.success('2FA desactivado');
        setDisableMode(false);
        setVerificationCode('');
        fetchStatus();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setProcessing(false);
    }
  };

  const regenerateBackupCodes = async () => {
    if (!verificationCode || verificationCode.length < 6) {
      toast.error('Introduce un código válido de 6 dígitos');
      return;
    }

    try {
      setProcessing(true);
      const response = await axios.post(
        `${API_URL}/api/admin/2fa/regenerate-backup-codes`,
        { code: verificationCode },
        { headers }
      );
      
      if (response.data.success) {
        setBackupCodes(response.data.backup_codes);
        setShowBackupCodes(true);
        setVerificationCode('');
        toast.success('Códigos de respaldo regenerados');
        fetchStatus();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al regenerar códigos');
    } finally {
      setProcessing(false);
    }
  };

  const copyToClipboard = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(index);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      toast.error('Error al copiar');
    }
  };

  const copyAllBackupCodes = async () => {
    const allCodes = backupCodes.join('\n');
    try {
      await navigator.clipboard.writeText(allCodes);
      toast.success('Todos los códigos copiados');
    } catch (err) {
      toast.error('Error al copiar');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin text-emerald-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-xl ${status.enabled ? 'bg-emerald-900/50' : 'bg-gray-800'}`}>
            {status.enabled ? (
              <ShieldCheck className="w-6 h-6 text-emerald-400" />
            ) : (
              <Shield className="w-6 h-6 text-gray-400" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">
              Autenticación de Dos Factores (2FA)
            </h3>
            <p className="text-sm text-gray-400">
              {status.enabled 
                ? `Activo • ${status.backup_codes_remaining} códigos de respaldo restantes`
                : 'Añade una capa extra de seguridad a tu cuenta'}
            </p>
          </div>
        </div>
        
        <div className={`px-3 py-1 rounded-full text-sm font-medium ${
          status.enabled 
            ? 'bg-emerald-900/50 text-emerald-400' 
            : 'bg-yellow-900/50 text-yellow-400'
        }`}>
          {status.enabled ? 'Activado' : 'Desactivado'}
        </div>
      </div>

      {/* Status Card */}
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
        {!status.enabled && !setupMode && (
          <div className="text-center">
            <Smartphone className="w-12 h-12 mx-auto mb-4 text-gray-500" />
            <h4 className="text-white font-medium mb-2">Protege tu cuenta con 2FA</h4>
            <p className="text-gray-400 text-sm mb-6 max-w-md mx-auto">
              Usa una app de autenticación como Google Authenticator, Authy o Microsoft Authenticator 
              para generar códigos temporales al iniciar sesión.
            </p>
            <Button
              onClick={startSetup}
              disabled={processing}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="enable-2fa-btn"
            >
              {processing ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Lock className="w-4 h-4 mr-2" />
              )}
              Activar 2FA
            </Button>
          </div>
        )}

        {status.enabled && !disableMode && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-emerald-900/20 rounded-lg border border-emerald-700/30">
              <ShieldCheck className="w-5 h-5 text-emerald-400" />
              <div className="flex-1">
                <p className="text-emerald-400 font-medium">2FA está activo</p>
                <p className="text-sm text-gray-400">
                  Tu cuenta está protegida con autenticación de dos factores
                </p>
              </div>
            </div>

            {status.backup_codes_remaining <= 2 && (
              <div className="flex items-center gap-3 p-4 bg-amber-900/20 rounded-lg border border-amber-700/30">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                <div className="flex-1">
                  <p className="text-amber-400 font-medium">Pocos códigos de respaldo</p>
                  <p className="text-sm text-gray-400">
                    Te quedan solo {status.backup_codes_remaining} códigos. Regenera nuevos códigos.
                  </p>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs text-gray-500 mb-1">Código de autenticador</label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-center text-xl tracking-widest font-mono focus:ring-2 focus:ring-emerald-500"
                  maxLength={6}
                />
              </div>
              <div className="flex items-end gap-2">
                <Button
                  onClick={regenerateBackupCodes}
                  disabled={processing || verificationCode.length < 6}
                  variant="outline"
                  className="text-sm"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${processing ? 'animate-spin' : ''}`} />
                  Regenerar Códigos
                </Button>
                <Button
                  onClick={() => setDisableMode(true)}
                  variant="outline"
                  className="text-sm text-red-400 border-red-400/50 hover:bg-red-900/30"
                >
                  <Unlock className="w-4 h-4 mr-2" />
                  Desactivar
                </Button>
              </div>
            </div>
          </div>
        )}

        {disableMode && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-red-900/20 rounded-lg border border-red-700/30">
              <ShieldOff className="w-5 h-5 text-red-400" />
              <div className="flex-1">
                <p className="text-red-400 font-medium">¿Desactivar 2FA?</p>
                <p className="text-sm text-gray-400">
                  Tu cuenta será menos segura sin la autenticación de dos factores
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs text-gray-500 mb-1">Introduce tu código 2FA para confirmar</label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-center text-xl tracking-widest font-mono focus:ring-2 focus:ring-red-500"
                  maxLength={6}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => { setDisableMode(false); setVerificationCode(''); }}
                  variant="outline"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={disable2FA}
                  disabled={processing || verificationCode.length < 6}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {processing ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ShieldOff className="w-4 h-4 mr-2" />
                  )}
                  Confirmar Desactivación
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Setup Modal */}
      <AnimatePresence>
        {setupMode && setupData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gray-900 rounded-2xl w-full max-w-md border border-gray-700 overflow-hidden"
            >
              <div className="p-6 border-b border-gray-800">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <QrCode className="w-5 h-5 text-emerald-400" />
                    Configurar 2FA
                  </h3>
                  <button
                    onClick={() => { setSetupMode(false); setSetupData(null); setVerificationCode(''); }}
                    className="p-2 hover:bg-gray-800 rounded-lg"
                  >
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Step 1: QR Code */}
                <div className="text-center">
                  <p className="text-sm text-gray-400 mb-4">
                    Escanea este código QR con tu app de autenticación
                  </p>
                  <div className="inline-block p-4 bg-white rounded-xl">
                    <img 
                      src={setupData.qr_code} 
                      alt="QR Code for 2FA"
                      className="w-48 h-48"
                    />
                  </div>
                </div>

                {/* Manual Entry */}
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-2">O introduce este código manualmente:</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-gray-900 rounded text-emerald-400 font-mono text-sm break-all">
                      {setupData.manual_entry_key}
                    </code>
                    <button
                      onClick={() => copyToClipboard(setupData.manual_entry_key, 'secret')}
                      className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      {copiedCode === 'secret' ? (
                        <Check className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Step 2: Verify */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    Introduce el código de 6 dígitos de tu app
                  </label>
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-center text-2xl tracking-widest font-mono focus:ring-2 focus:ring-emerald-500"
                    maxLength={6}
                    autoFocus
                    data-testid="2fa-verification-code"
                  />
                </div>
              </div>

              <div className="p-6 border-t border-gray-800 flex gap-3">
                <Button
                  onClick={() => { setSetupMode(false); setSetupData(null); setVerificationCode(''); }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={confirmSetup}
                  disabled={processing || verificationCode.length < 6}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="confirm-2fa-btn"
                >
                  {processing ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ShieldCheck className="w-4 h-4 mr-2" />
                  )}
                  Verificar y Activar
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Backup Codes Modal */}
      <AnimatePresence>
        {showBackupCodes && backupCodes.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gray-900 rounded-2xl w-full max-w-md border border-gray-700 overflow-hidden"
            >
              <div className="p-6 border-b border-gray-800">
                <div className="flex items-center gap-3">
                  <Key className="w-5 h-5 text-amber-400" />
                  <div>
                    <h3 className="text-lg font-bold text-white">Códigos de Respaldo</h3>
                    <p className="text-sm text-gray-400">Guarda estos códigos en un lugar seguro</p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div className="bg-amber-900/20 rounded-lg p-4 border border-amber-700/30">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-300">
                      <strong>¡Importante!</strong> Estos códigos solo se muestran una vez. 
                      Guárdalos en un lugar seguro. Cada código solo puede usarse una vez.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {backupCodes.map((code, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between px-3 py-2 bg-gray-800 rounded-lg"
                    >
                      <code className="text-white font-mono text-sm">{code}</code>
                      <button
                        onClick={() => copyToClipboard(code, index)}
                        className="p-1 hover:bg-gray-700 rounded transition-colors"
                      >
                        {copiedCode === index ? (
                          <Check className="w-3 h-3 text-emerald-400" />
                        ) : (
                          <Copy className="w-3 h-3 text-gray-400" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>

                <Button
                  onClick={copyAllBackupCodes}
                  variant="outline"
                  className="w-full"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar Todos los Códigos
                </Button>
              </div>

              <div className="p-6 border-t border-gray-800">
                <Button
                  onClick={() => { setShowBackupCodes(false); setBackupCodes([]); }}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  <Check className="w-4 h-4 mr-2" />
                  He Guardado los Códigos
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default TwoFactorSettings;
